/**
 * Popover for picking a section duration: three minute presets or a custom whole number of minutes.
 *
 * Used by clicking a GapDuration label inside a heading while editing. A preset applies on click; a custom
 * entry applies on Enter or when clicking away, Escape cancels, and a button removes the duration altogether.
 */

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { themeConfig } from '@gap/shared/theme'
import useStore from 'reusable/mobx-store-utils/useStore'
import { formatDuration, parseMinutes } from '../plugins/Duration/duration-utils'
import { GroupDivider } from './GroupHeader'
import PopoverSurface, { PopoverPosition } from './PopoverSurface'

interface DurationSubmenuPopoverProps {
   anchor: PopoverPosition
   onSelect: (minutes: number) => void
   onRemove: () => void
   onClose: () => void
}

export const DURATION_PRESETS_IN_MINUTES = [5, 15, 30]

export class DurationSubmenuPopoverStore {
   sp: DurationSubmenuPopoverProps
   customMinutes = ''

   constructor(sp: DurationSubmenuPopoverProps) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get parsedCustomMinutes() {
      return parseMinutes(this.customMinutes)
   }

   get canApplyCustom() {
      return this.parsedCustomMinutes !== null
   }

   handlePresetMouseDown = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      const minutes = parseMinutes(event.currentTarget.getAttribute('data-minutes') ?? '')
      if (minutes !== null) this.sp.onSelect(minutes)
   }

   handleCustomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      this.customMinutes = event.target.value
   }

   handleCustomKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      this.applyCustom()
   }

   applyCustom = () => {
      const minutes = this.parsedCustomMinutes
      if (minutes !== null) this.sp.onSelect(minutes)
   }

   handleRemove = () => {
      this.sp.onRemove()
   }

   handleDismiss = () => {
      if (this.canApplyCustom) {
         this.applyCustom()
      } else {
         this.sp.onClose()
      }
   }

   handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      this.sp.onClose()
   }
}

const DurationSubmenuPopover = observer(function DurationSubmenuPopover(props: DurationSubmenuPopoverProps) {
   const durationSubmenuPopoverStore = useStore(sp => new DurationSubmenuPopoverStore(sp), props)

   useEffect(() => {
      window.addEventListener('keydown', durationSubmenuPopoverStore.handleWindowKeyDown, true)
      return () => window.removeEventListener('keydown', durationSubmenuPopoverStore.handleWindowKeyDown, true)
   }, [durationSubmenuPopoverStore.handleWindowKeyDown])

   return <PopoverSurface anchor={props.anchor} width={240} maxHeight={280} onDismiss={durationSubmenuPopoverStore.handleDismiss}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '4px 8px' }}>
         {DURATION_PRESETS_IN_MINUTES.map(minutes => (
            <Box
               key={minutes}
               role="option"
               data-minutes={minutes}
               onMouseDown={durationSubmenuPopoverStore.handlePresetMouseDown}
               sx={{
                  padding: '6px 8px',
                  fontSize: 14,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: themeConfig.primary[800],
                  '&:hover': { bgcolor: 'action.hover' },
               }}
            >
               {formatDuration(minutes)}
            </Box>
         ))}
      </Box>
      <GroupDivider />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, padding: '8px 16px 8px' }}>
         <TextField
            size="small"
            label="Custom"
            placeholder="Minutes"
            type="number"
            fullWidth
            value={durationSubmenuPopoverStore.customMinutes}
            onChange={durationSubmenuPopoverStore.handleCustomChange}
            onKeyDown={durationSubmenuPopoverStore.handleCustomKeyDown}
            slotProps={{ htmlInput: { min: 1, step: 1, inputMode: 'numeric' } }}
         />
         <Button size="small" variant="text" onClick={durationSubmenuPopoverStore.handleRemove}>
            Remove duration
         </Button>
      </Box>
   </PopoverSurface>
})

export default DurationSubmenuPopover
